import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Container,
    Dialog,
    DialogContent,
    DialogActions,
    CircularProgress,
} from '@mui/material';
import { getPendingMessages, approveMessage, rejectMessage, subscribeToPendingMessages } from './firebaseService';

const MessageApproval = () => {
    const { workspaceId } = useParams();
    const [pendingMessages, setPendingMessages] = useState([]);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // 실시간으로 승인 대기 중인 메시지 구독
        const unsubscribe = subscribeToPendingMessages(workspaceId, (messages) => {
            console.log(messages);
            setPendingMessages(messages);
        });

        return () => {
            unsubscribe();
        };
    }, [workspaceId]);

    const handleApprove = async (messageId) => {
        setLoading(true);
        try {
            await approveMessage(workspaceId, messageId);
        } catch (error) {
            console.error('메시지 승인 오류:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async (messageId) => {
        setLoading(true);
        try {
            await rejectMessage(workspaceId, messageId);
        } catch (error) {
            console.error('메시지 거부 오류:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (message) => {
        setSelectedMessage(message);
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedMessage(null);
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Typography variant="h4" gutterBottom>
                메시지 승인 관리
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                승인 대기 중인 메시지 목록입니다. 각 메시지를 검토하고 승인 또는 거부하세요.
            </Typography>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <CircularProgress />
                </Box>
            )}

            {pendingMessages.length === 0 ? (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                    승인 대기 중인 메시지가 없습니다.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {pendingMessages.map((message) => (
                        <Card key={message.id} sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {message.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    {message.affiliation} {message.position}
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {message.message}
                                </Typography>
                                {message.image && (
                                    <Box sx={{ mb: 2 }}>
                                        <img
                                            src={message.image}
                                            alt="첨부 이미지"
                                            style={{ maxWidth: '100%', maxHeight: '200px' }}
                                        />
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => handleOpenDialog(message)}
                                    >
                                        상세보기
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={() => handleApprove(message.id)}
                                        disabled={loading}
                                    >
                                        승인
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={() => handleReject(message.id)}
                                        disabled={loading}
                                    >
                                        거부
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}

            {/* 메시지 상세 다이얼로그 */}
            <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogContent>
                    {selectedMessage && (
                        <>
                            <Typography variant="h6" gutterBottom>
                                {selectedMessage.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {selectedMessage.affiliation} {selectedMessage.position}
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {selectedMessage.message}
                            </Typography>
                            {selectedMessage.image && (
                                <Box sx={{ mb: 2 }}>
                                    <img
                                        src={selectedMessage.image}
                                        alt="첨부 이미지"
                                        style={{ maxWidth: '100%', maxHeight: '300px' }}
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>닫기</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default MessageApproval; 